import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';

export function MagicLinkPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loginWithMagicLink } = useAuth();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [error, setError] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setStatus('error');
      setError('No token provided');
      return;
    }

    async function verifyToken() {
      try {
        await loginWithMagicLink(token!);
        setStatus('success');
        // Redirect after short delay
        setTimeout(() => {
          navigate('/');
        }, 1500);
      } catch (err) {
        setStatus('error');
        setError(err instanceof Error ? err.message : 'Invalid or expired magic link');
      }
    }

    verifyToken();
  }, [searchParams, loginWithMagicLink, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary-50 dark:bg-secondary-950 p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-500">
            <span className="text-xl font-bold text-white">DF</span>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-secondary-900 dark:text-white">
              DevFactory
            </h1>
            <p className="text-sm text-secondary-500 dark:text-secondary-400">
              AI-Native No-Code Platform
            </p>
          </div>
        </div>

        <Card>
          <CardHeader className="text-center">
            <CardTitle>
              {status === 'loading' && 'Verifying...'}
              {status === 'success' && 'Welcome!'}
              {status === 'error' && 'Oops!'}
            </CardTitle>
            <CardDescription>
              {status === 'loading' && 'Please wait while we verify your magic link'}
              {status === 'success' && 'You have been successfully logged in'}
              {status === 'error' && 'There was a problem with your magic link'}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center gap-4">
            {status === 'loading' && (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
              </div>
            )}

            {status === 'success' && (
              <div className="py-4 text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900">
                  <svg
                    className="h-6 w-6 text-green-600 dark:text-green-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <p className="text-sm text-muted-foreground">
                  Redirecting you to the dashboard...
                </p>
              </div>
            )}

            {status === 'error' && (
              <div className="py-4 text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900">
                  <svg
                    className="h-6 w-6 text-red-600 dark:text-red-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </div>
                <p className="mb-4 text-sm text-red-600 dark:text-red-400">{error}</p>
                <Button onClick={() => navigate('/login')}>Back to Login</Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default MagicLinkPage;
